import { TFunction } from "i18next";
import {
  ComplianceValue,
  ConsumptionSourceName,
  RecipeComplianceDiffSourceName,
  StrategyDashboardComplianceData,
  UnitType,
} from "src/store/api/generatedApi";

export const buildDiffSourceReferenceMap = (
  values: ComplianceValue[]
): Map<RecipeComplianceDiffSourceName, ConsumptionSourceName> => {
  const map = new Map<RecipeComplianceDiffSourceName, ConsumptionSourceName>();
  for (const value of values) {
    if (!map.has(value.recipe_compliance_diff_source)) {
      map.set(value.recipe_compliance_diff_source, value.source_a);
    }
  }
  return map;
};

export const calculateDiff = (
  materialName: string,
  compliance: StrategyDashboardComplianceData,
  unit: UnitType,
  t: TFunction
): number => {
  const isTotal = materialName === t("total");

  const getQuantityForSource = (sourceName: ConsumptionSourceName) => {
    const value = compliance.values.find((v) => v.source === sourceName);
    const quantities = isTotal
      ? value?.totals
      : value?.consumption.find((c) => c.material_name === materialName)
          ?.quantities;
    return quantities?.find((q) => q.unit === unit);
  };

  const firstValue =
    getQuantityForSource(compliance.values[0]?.source_a ?? "")?.value ?? 0;
  const secondValue =
    getQuantityForSource(compliance.values[0]?.source_b ?? "")?.value ?? 0;

  return secondValue - firstValue;
};

export const getMaxDiff = (
  compliance: StrategyDashboardComplianceData,
  unit: UnitType,
  t: TFunction
): number => {
  const materialNames = new Set(
    compliance.values
      .map((v) => v.consumption.map((v) => v.material_name))
      .flat()
  );

  const diffs = Array.from(materialNames).map((materialName) => {
    return Math.abs(calculateDiff(materialName, compliance, unit, t));
  });

  return Math.max(...diffs);
};

export const getTotalMassFractionDiff = (
  compliance: StrategyDashboardComplianceData,
  t: TFunction
): number => {
  const materialNames = Array.from(
    new Set(
      compliance.values
        .map((v) => v.consumption.map((v) => v.material_name))
        .flat()
    )
  );

  return materialNames.reduce(
    (acc, materialName) =>
      acc +
      Math.abs(calculateDiff(materialName, compliance, "mass_fraction", t)),
    0
  );
};

export const calculateComplianceScore = (
  compliance: StrategyDashboardComplianceData,
  t: TFunction,
  formatNumber: (
    decimals: number,
    fixed?: boolean
  ) => (value?: number | null) => string
): string => {
  if (compliance.values.length === 0) {
    return "-";
  }
  const score = (1 - getTotalMassFractionDiff(compliance, t)) * 100;
  return `${formatNumber(1, true)(score)}%`;
};

export interface CrewComplianceScore {
  crewId: string;
  compliancePercent: number;
}

export interface CompliancePodiumScores {
  crewScores: CrewComplianceScore[];
  overallCompliancePercent: number | undefined;
}

const ALL_CREW_ID = "All";
const ALL_PRODUCT_GROUP = "All";

export interface DiffRow {
  id: string;
  materialName: string;
  diffs: Record<string, number>;
  outlierHeatIdsByCrew: Record<string, string[]>;
}

const getMaterialQuantity = (
  value: ComplianceValue | undefined,
  materialName: string,
  unit: UnitType
): number => {
  if (!value) return 0;
  const consumption = value.consumption.find(
    (c) => c.material_name === materialName
  );
  return consumption?.quantities.find((q) => q.unit === unit)?.value ?? 0;
};

const collectOutlierHeatIds = (
  pair: { sourceA?: ComplianceValue; sourceB?: ComplianceValue } | undefined,
  materialName: string
): string[] => {
  const ids = new Set<string>();
  for (const value of [pair?.sourceA, pair?.sourceB]) {
    const consumption = value?.consumption.find(
      (c) => c.material_name === materialName
    );
    for (const id of consumption?.outlier_heat_ids ?? []) {
      ids.add(id);
    }
  }
  return [...ids];
};

/**
 * Per-crew, per-material `source_b - source_a` consumption diffs in the given
 * unit, plus the summed absolute total and the cost diff per crew. Shared by the
 * ranking-diff grid, the podium scores, and the overview card so the numbers
 * stay in lockstep.
 */
export const computeComplianceDiffs = (
  values: ComplianceValue[],
  crewIds: string[],
  unit: UnitType
): {
  rows: DiffRow[];
  totalDiffs: Record<string, number>;
  costDiffs: Record<string, number> | null;
  maxAbsDiff: number;
} => {
  const byCrew = new Map<
    string,
    { sourceA?: ComplianceValue; sourceB?: ComplianceValue }
  >();
  for (const value of values) {
    const entry = byCrew.get(value.crew_id) ?? {};
    if (value.source === value.source_a) entry.sourceA = value;
    else if (value.source === value.source_b) entry.sourceB = value;
    byCrew.set(value.crew_id, entry);
  }

  const materialNames: string[] = [];
  const seen = new Set<string>();
  for (const value of values) {
    for (const consumption of value.consumption) {
      if (!seen.has(consumption.material_name)) {
        materialNames.push(consumption.material_name);
        seen.add(consumption.material_name);
      }
    }
  }

  let maxAbsDiff = 0;
  const rows: DiffRow[] = materialNames.map((materialName) => {
    const diffs: Record<string, number> = {};
    const outlierHeatIdsByCrew: Record<string, string[]> = {};
    for (const crewId of crewIds) {
      const pair = byCrew.get(crewId);
      const diff =
        getMaterialQuantity(pair?.sourceB, materialName, unit) -
        getMaterialQuantity(pair?.sourceA, materialName, unit);
      diffs[crewId] = diff;
      maxAbsDiff = Math.max(maxAbsDiff, Math.abs(diff));
      outlierHeatIdsByCrew[crewId] = collectOutlierHeatIds(pair, materialName);
    }
    return { id: materialName, materialName, diffs, outlierHeatIdsByCrew };
  });

  const totalDiffs: Record<string, number> = {};
  for (const crewId of crewIds) {
    totalDiffs[crewId] = rows.reduce(
      (sum, row) => sum + Math.abs(row.diffs[crewId] ?? 0),
      0
    );
  }

  const hasCostData = values.some((value) => value.specific_cost !== null);
  let costDiffs: Record<string, number> | null = null;
  if (hasCostData) {
    costDiffs = {};
    for (const crewId of crewIds) {
      const pair = byCrew.get(crewId);
      costDiffs[crewId] =
        (pair?.sourceB?.specific_cost ?? 0) -
        (pair?.sourceA?.specific_cost ?? 0);
    }
  }

  return { rows, totalDiffs, costDiffs, maxAbsDiff };
};

/**
 * Per-crew and overall recipe-compliance percentages for the podium.
 *
 * Compliance is `(1 - total absolute mass-fraction diff) * 100` per crew; the
 * `"All"` crew, when present, yields the overall score.
 */
export const computeCompliancePodiumScores = (
  values: ComplianceValue[],
  crewIds: string[]
): CompliancePodiumScores => {
  const { totalDiffs } = computeComplianceDiffs(
    values,
    crewIds,
    "mass_fraction"
  );

  const crewScores = crewIds
    .filter((crewId) => crewId !== ALL_CREW_ID)
    .map((crewId) => ({
      crewId,
      compliancePercent: (1 - (totalDiffs[crewId] ?? 0)) * 100,
    }));

  const allCrewDiff = totalDiffs[ALL_CREW_ID];
  const overallCompliancePercent =
    allCrewDiff === undefined ? undefined : (1 - allCrewDiff) * 100;

  return { crewScores, overallCompliancePercent };
};

/**
 * Derive podium scores from raw compliance data using the same default view as
 * the ranking compliance table: the "All" product group and the first available
 * diff source, aggregated across every crew.
 */
export const deriveCompliancePodiumScores = (
  data: StrategyDashboardComplianceData
): CompliancePodiumScores => {
  const diffSource = data.available_recipe_compliance_diff_sources[0];
  const values = data.values.filter(
    (value) =>
      value.product_group_name === ALL_PRODUCT_GROUP &&
      value.recipe_compliance_diff_source === diffSource
  );
  return computeCompliancePodiumScores(values, data.available_crews);
};
